import type React from "react";
import { useCallback, useState } from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { Upload } from "lucide-react";
import { useArticleStore } from "../store/useArticleStore";
import type { Photo } from "../types";
import { generateId } from "../utils/generateId";
import { MAX_FILES_PER_UPLOAD, MAX_FILE_SIZE_BYTES } from "../constants/imageProcessing";

export const PhotoUploader: React.FC = () => {
  const { article, setPhotos, setArticle } = useArticleStore();
  const [userNotes, setUserNotes] = useState("");
  const [uploadError, setUploadError] = useState<string>();

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Limit the number of files that can be processed at once
      const filesToProcess = acceptedFiles.slice(0, MAX_FILES_PER_UPLOAD);

      const messages: string[] = [];
      if (acceptedFiles.length > MAX_FILES_PER_UPLOAD) {
        messages.push(`Only the first ${MAX_FILES_PER_UPLOAD} accepted photos were added.`);
      }
      if (rejectedFiles.length > 0) {
        const rejectedNames = rejectedFiles.map(({ file }) => file.name).join(", ");
        messages.push(
          `${rejectedFiles.length} ${rejectedFiles.length === 1 ? "file was" : "files were"} not added (${rejectedNames}). Use JPEG or PNG files up to ${MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB.`,
        );
      }
      setUploadError(messages.join(" ") || undefined);

      try {
        const newPhotos: Photo[] = await Promise.all(
          filesToProcess.map(async (file) => {
            try {
              return {
                id: generateId(),
                file,
                preview: URL.createObjectURL(file),
                isProcessing: false,
                userNotes,
              };
            } catch (error) {
              console.error("Failed to create preview for file:", file.name, error);
              throw error;
            }
          }),
        );

        // Update the store with new photos
        const updatedArticle = {
          ...article,
          sections: [...article.sections, ...newPhotos],
        };
        setPhotos(updatedArticle.sections);
      } catch (error) {
        console.error("Failed to process dropped files:", error);
        setArticle({
          error:
            "Failed to process some of the uploaded photos. Please try again with fewer photos.",
        });
        setUploadError("Some photos could not be added. Try again with fewer or smaller files.");
      }
    },
    [article, setPhotos, setArticle, userNotes],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
    },
    maxSize: MAX_FILE_SIZE_BYTES,
    multiple: true,
    disabled: article.isLoading,
  });

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
          ${article.isLoading ? "cursor-not-allowed bg-gray-50 opacity-60" : "cursor-pointer"}
          ${isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400"}`}
      >
        <input {...getInputProps()} />
        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">
          {isDragActive ? "Drop the photos here..." : "Drag & drop photos here, or click to select"}
        </p>
        <p className="text-xs text-gray-500 mt-1">Supported formats: JPEG, PNG</p>
        <p className="text-xs text-gray-500 mt-1">
          Up to {MAX_FILES_PER_UPLOAD} photos per upload, {MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB
          each
        </p>
      </div>

      {uploadError && (
        <p role="alert" className="text-sm text-red-600">
          {uploadError}
        </p>
      )}

      <div className="space-y-2">
        <label htmlFor="photo-notes" className="block text-sm font-medium text-gray-700">
          Notes for the next upload
        </label>
        <textarea
          id="photo-notes"
          value={userNotes}
          onChange={(e) => setUserNotes(e.target.value)}
          maxLength={4000}
          placeholder="Add any additional context or notes about these photos..."
          disabled={article.isLoading}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm min-h-[80px]"
        />
        <p className="text-xs text-gray-500">
          These notes will be attached to the photos you add next.
        </p>
      </div>
    </div>
  );
};
